/**
 * EmojiPicker - Professional emoji picker component
 * Clean, responsive design with categorized emojis
 */

import { useState, useRef, useEffect } from 'react'
import { Search, Smile, Heart, Pizza, Car, Zap, Lightbulb, Flag } from 'lucide-react'

export interface EmojiData {
  emoji: string
  name: string
  category: string
}

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void
  onClose: () => void
  isOpen: boolean
  position?: 'top' | 'bottom'
}

const EMOJI_CATEGORIES = {
  smileys: {
    name: 'Smileys & People',
    icon: Smile,
    emojis: [
      { emoji: '😀', name: 'grinning face' },
      { emoji: '😁', name: 'beaming face' },
      { emoji: '😂', name: 'face with tears of joy' },
      { emoji: '🤣', name: 'rolling on floor laughing' },
      { emoji: '😃', name: 'grinning face with big eyes' },
      { emoji: '😄', name: 'grinning face with smiling eyes' },
      { emoji: '😅', name: 'grinning face with sweat' },
      { emoji: '😆', name: 'grinning squinting face' },
      { emoji: '😉', name: 'winking face' },
      { emoji: '😊', name: 'smiling face with smiling eyes' },
      { emoji: '😋', name: 'face savoring food' },
      { emoji: '😎', name: 'smiling face with sunglasses' },
      { emoji: '😍', name: 'smiling face with heart-eyes' },
      { emoji: '🥰', name: 'smiling face with hearts' },
      { emoji: '😘', name: 'face blowing a kiss' },
      { emoji: '😗', name: 'kissing face' },
      { emoji: '😙', name: 'kissing face with smiling eyes' },
      { emoji: '😚', name: 'kissing face with closed eyes' },
      { emoji: '🙂', name: 'slightly smiling face' },
      { emoji: '🤗', name: 'hugging face' },
      { emoji: '🤩', name: 'star-struck' },
      { emoji: '🤔', name: 'thinking face' },
      { emoji: '🤨', name: 'face with raised eyebrow' },
      { emoji: '😐', name: 'neutral face' },
      { emoji: '😑', name: 'expressionless face' },
      { emoji: '🙄', name: 'face with rolling eyes' },
      { emoji: '😏', name: 'smirking face' },
      { emoji: '😣', name: 'persevering face' },
      { emoji: '😥', name: 'sad but relieved face' },
      { emoji: '😮', name: 'face with open mouth' },
      { emoji: '🤐', name: 'zipper-mouth face' },
      { emoji: '😯', name: 'hushed face' },
      { emoji: '😪', name: 'sleepy face' },
      { emoji: '😫', name: 'tired face' },
      { emoji: '🥱', name: 'yawning face' },
      { emoji: '😴', name: 'sleeping face' },
      { emoji: '😌', name: 'relieved face' },
      { emoji: '😛', name: 'face with tongue' },
      { emoji: '😜', name: 'winking face with tongue' },
      { emoji: '😝', name: 'squinting face with tongue' },
      { emoji: '🤤', name: 'drooling face' },
      { emoji: '😒', name: 'unamused face' },
      { emoji: '😓', name: 'downcast face with sweat' },
      { emoji: '😔', name: 'pensive face' },
      { emoji: '😕', name: 'confused face' },
      { emoji: '🙃', name: 'upside-down face' },
      { emoji: '🤑', name: 'money-mouth face' },
      { emoji: '😲', name: 'astonished face' },
      { emoji: '☹️', name: 'frowning face' },
      { emoji: '🙁', name: 'slightly frowning face' },
      { emoji: '😖', name: 'confounded face' },
      { emoji: '😞', name: 'disappointed face' },
      { emoji: '😟', name: 'worried face' },
      { emoji: '😤', name: 'face with steam from nose' },
      { emoji: '😢', name: 'crying face' },
      { emoji: '😭', name: 'loudly crying face' },
      { emoji: '😦', name: 'frowning face with open mouth' },
      { emoji: '😧', name: 'anguished face' },
      { emoji: '😨', name: 'fearful face' },
      { emoji: '😩', name: 'weary face' },
      { emoji: '🤯', name: 'exploding head' },
      { emoji: '😬', name: 'grimacing face' },
      { emoji: '😰', name: 'anxious face with sweat' },
      { emoji: '😱', name: 'face screaming in fear' },
      { emoji: '🥵', name: 'hot face' },
      { emoji: '🥶', name: 'cold face' },
      { emoji: '😳', name: 'flushed face' },
      { emoji: '🤪', name: 'zany face' },
      { emoji: '😵', name: 'dizzy face' },
      { emoji: '🥴', name: 'woozy face' },
      { emoji: '😷', name: 'face with medical mask' },
      { emoji: '🤒', name: 'face with thermometer' },
      { emoji: '🤕', name: 'face with head-bandage' },
      { emoji: '🤢', name: 'nauseated face' },
      { emoji: '🤮', name: 'face vomiting' },
      { emoji: '🤧', name: 'sneezing face' },
      { emoji: '😇', name: 'smiling face with halo' }
    ]
  },
  hearts: {
    name: 'Hearts & Love',
    icon: Heart,
    emojis: [
      { emoji: '❤️', name: 'red heart' },
      { emoji: '🧡', name: 'orange heart' },
      { emoji: '💛', name: 'yellow heart' },
      { emoji: '💚', name: 'green heart' },
      { emoji: '💙', name: 'blue heart' },
      { emoji: '💜', name: 'purple heart' },
      { emoji: '🖤', name: 'black heart' },
      { emoji: '🤍', name: 'white heart' },
      { emoji: '🤎', name: 'brown heart' },
      { emoji: '💔', name: 'broken heart' },
      { emoji: '❣️', name: 'heart exclamation' },
      { emoji: '💕', name: 'two hearts' },
      { emoji: '💞', name: 'revolving hearts' },
      { emoji: '💓', name: 'beating heart' },
      { emoji: '💗', name: 'growing heart' },
      { emoji: '💖', name: 'sparkling heart' },
      { emoji: '💘', name: 'heart with arrow' },
      { emoji: '💝', name: 'heart with ribbon' },
      { emoji: '💟', name: 'heart decoration' },
      { emoji: '💌', name: 'love letter' },
      { emoji: '💋', name: 'kiss mark' },
      { emoji: '💍', name: 'ring' },
      { emoji: '💎', name: 'gem stone' }
    ]
  },
  food: {
    name: 'Food & Drink',
    icon: Pizza,
    emojis: [
      { emoji: '🍎', name: 'red apple' },
      { emoji: '🍌', name: 'banana' },
      { emoji: '🍓', name: 'strawberry' },
      { emoji: '🍊', name: 'tangerine' },
      { emoji: '🍉', name: 'watermelon' },
      { emoji: '🍇', name: 'grapes' },
      { emoji: '🥝', name: 'kiwi fruit' },
      { emoji: '🍅', name: 'tomato' },
      { emoji: '🥕', name: 'carrot' },
      { emoji: '🌽', name: 'corn' },
      { emoji: '🥖', name: 'baguette bread' },
      { emoji: '🧀', name: 'cheese wedge' },
      { emoji: '🥚', name: 'egg' },
      { emoji: '🍳', name: 'cooking' },
      { emoji: '🥓', name: 'bacon' },
      { emoji: '🍔', name: 'hamburger' },
      { emoji: '🍟', name: 'french fries' },
      { emoji: '🌭', name: 'hot dog' },
      { emoji: '🍕', name: 'pizza' },
      { emoji: '🌮', name: 'taco' },
      { emoji: '🌯', name: 'burrito' },
      { emoji: '🥙', name: 'stuffed flatbread' },
      { emoji: '🥗', name: 'green salad' },
      { emoji: '🍝', name: 'spaghetti' },
      { emoji: '🍜', name: 'steaming bowl' },
      { emoji: '🍲', name: 'pot of food' },
      { emoji: '🍱', name: 'bento box' },
      { emoji: '🍣', name: 'sushi' },
      { emoji: '🍤', name: 'fried shrimp' },
      { emoji: '🍦', name: 'soft ice cream' },
      { emoji: '🍰', name: 'shortcake' },
      { emoji: '🎂', name: 'birthday cake' },
      { emoji: '🍮', name: 'custard' },
      { emoji: '🍭', name: 'lollipop' },
      { emoji: '🍬', name: 'candy' },
      { emoji: '🍫', name: 'chocolate bar' },
      { emoji: '🍿', name: 'popcorn' },
      { emoji: '🍩', name: 'doughnut' },
      { emoji: '🍪', name: 'cookie' },
      { emoji: '☕', name: 'hot beverage' },
      { emoji: '🍵', name: 'teacup without handle' },
      { emoji: '🥤', name: 'cup with straw' },
      { emoji: '🍶', name: 'sake' },
      { emoji: '🍺', name: 'beer mug' },
      { emoji: '🍻', name: 'clinking beer mugs' },
      { emoji: '🥂', name: 'clinking glasses' },
      { emoji: '🍷', name: 'wine glass' },
      { emoji: '🥃', name: 'tumbler glass' },
      { emoji: '🍸', name: 'cocktail glass' },
      { emoji: '🍹', name: 'tropical drink' }
    ]
  },
  activity: {
    name: 'Activities',
    icon: Zap,
    emojis: [
      { emoji: '⚽', name: 'soccer ball' },
      { emoji: '🏀', name: 'basketball' },
      { emoji: '🏈', name: 'american football' },
      { emoji: '⚾', name: 'baseball' },
      { emoji: '🥎', name: 'softball' },
      { emoji: '🎾', name: 'tennis' },
      { emoji: '🏐', name: 'volleyball' },
      { emoji: '🏉', name: 'rugby football' },
      { emoji: '🥏', name: 'flying disc' },
      { emoji: '🎱', name: 'pool 8 ball' },
      { emoji: '🪀', name: 'yo-yo' },
      { emoji: '🏓', name: 'ping pong' },
      { emoji: '🏸', name: 'badminton' },
      { emoji: '🏒', name: 'ice hockey' },
      { emoji: '🏑', name: 'field hockey' },
      { emoji: '🥍', name: 'lacrosse' },
      { emoji: '🏏', name: 'cricket game' },
      { emoji: '🪃', name: 'boomerang' },
      { emoji: '🎣', name: 'fishing pole' },
      { emoji: '🤿', name: 'diving mask' },
      { emoji: '🎽', name: 'running shirt' },
      { emoji: '🎿', name: 'skis' },
      { emoji: '🛷', name: 'sled' },
      { emoji: '🥌', name: 'curling stone' },
      { emoji: '🎯', name: 'direct hit' },
      { emoji: '🪁', name: 'kite' },
      { emoji: '🎮', name: 'video game' },
      { emoji: '🕹️', name: 'joystick' },
      { emoji: '🎲', name: 'game die' },
      { emoji: '♠️', name: 'spade suit' },
      { emoji: '♥️', name: 'heart suit' },
      { emoji: '♦️', name: 'diamond suit' },
      { emoji: '♣️', name: 'club suit' },
      { emoji: '♟️', name: 'chess pawn' },
      { emoji: '🃏', name: 'joker' },
      { emoji: '🀄', name: 'mahjong red dragon' },
      { emoji: '🎴', name: 'flower playing cards' }
    ]
  },
  travel: {
    name: 'Travel & Places',
    icon: Car,
    emojis: [
      { emoji: '🚗', name: 'automobile' },
      { emoji: '🚕', name: 'taxi' },
      { emoji: '🚙', name: 'sport utility vehicle' },
      { emoji: '🚌', name: 'bus' },
      { emoji: '🚎', name: 'trolleybus' },
      { emoji: '🏎️', name: 'racing car' },
      { emoji: '🚓', name: 'police car' },
      { emoji: '🚑', name: 'ambulance' },
      { emoji: '🚒', name: 'fire engine' },
      { emoji: '🚐', name: 'minibus' },
      { emoji: '🛻', name: 'pickup truck' },
      { emoji: '🚚', name: 'delivery truck' },
      { emoji: '🚛', name: 'articulated lorry' },
      { emoji: '🚜', name: 'tractor' },
      { emoji: '🏍️', name: 'motorcycle' },
      { emoji: '🛵', name: 'motor scooter' },
      { emoji: '🚲', name: 'bicycle' },
      { emoji: '🛴', name: 'kick scooter' },
      { emoji: '🛹', name: 'skateboard' },
      { emoji: '🛼', name: 'roller skate' },
      { emoji: '🚁', name: 'helicopter' },
      { emoji: '✈️', name: 'airplane' },
      { emoji: '🛩️', name: 'small airplane' },
      { emoji: '🛫', name: 'airplane departure' },
      { emoji: '🛬', name: 'airplane arrival' },
      { emoji: '🪂', name: 'parachute' },
      { emoji: '💺', name: 'seat' },
      { emoji: '🚀', name: 'rocket' },
      { emoji: '🛸', name: 'flying saucer' },
      { emoji: '🚉', name: 'station' },
      { emoji: '🚞', name: 'mountain railway' },
      { emoji: '🚝', name: 'monorail' },
      { emoji: '🚄', name: 'high-speed train' },
      { emoji: '🚅', name: 'bullet train' },
      { emoji: '🚈', name: 'light rail' },
      { emoji: '🚇', name: 'metro' },
      { emoji: '🚆', name: 'train' },
      { emoji: '🚂', name: 'locomotive' },
      { emoji: '🚃', name: 'railway car' },
      { emoji: '🚋', name: 'tram' },
      { emoji: '🚊', name: 'tram' },
      { emoji: '🚝', name: 'monorail' },
      { emoji: '🚄', name: 'high-speed train' },
      { emoji: '⛵', name: 'sailboat' },
      { emoji: '🛥️', name: 'motor boat' },
      { emoji: '🚤', name: 'speedboat' },
      { emoji: '🛳️', name: 'passenger ship' },
      { emoji: '⛴️', name: 'ferry' },
      { emoji: '🚢', name: 'ship' }
    ]
  },
  objects: {
    name: 'Objects',
    icon: Lightbulb,
    emojis: [
      { emoji: '💡', name: 'light bulb' },
      { emoji: '🔦', name: 'flashlight' },
      { emoji: '🏮', name: 'red paper lantern' },
      { emoji: '🪔', name: 'diya lamp' },
      { emoji: '📱', name: 'mobile phone' },
      { emoji: '📞', name: 'telephone receiver' },
      { emoji: '☎️', name: 'telephone' },
      { emoji: '📟', name: 'pager' },
      { emoji: '📠', name: 'fax machine' },
      { emoji: '🔋', name: 'battery' },
      { emoji: '🪫', name: 'low battery' },
      { emoji: '🔌', name: 'electric plug' },
      { emoji: '💻', name: 'laptop' },
      { emoji: '🖥️', name: 'desktop computer' },
      { emoji: '🖨️', name: 'printer' },
      { emoji: '⌨️', name: 'keyboard' },
      { emoji: '🖱️', name: 'computer mouse' },
      { emoji: '🖲️', name: 'trackball' },
      { emoji: '💽', name: 'computer disk' },
      { emoji: '💾', name: 'floppy disk' },
      { emoji: '💿', name: 'optical disk' },
      { emoji: '📀', name: 'dvd' },
      { emoji: '🧮', name: 'abacus' },
      { emoji: '🎥', name: 'movie camera' },
      { emoji: '🎞️', name: 'film frames' },
      { emoji: '📽️', name: 'film projector' },
      { emoji: '🎬', name: 'clapper board' },
      { emoji: '📺', name: 'television' },
      { emoji: '📷', name: 'camera' },
      { emoji: '📸', name: 'camera with flash' },
      { emoji: '📹', name: 'video camera' },
      { emoji: '📼', name: 'videocassette' },
      { emoji: '🔍', name: 'magnifying glass tilted left' },
      { emoji: '🔎', name: 'magnifying glass tilted right' },
      { emoji: '🕯️', name: 'candle' },
      { emoji: '💰', name: 'money bag' },
      { emoji: '🪙', name: 'coin' },
      { emoji: '💴', name: 'yen banknote' },
      { emoji: '💵', name: 'dollar banknote' },
      { emoji: '💶', name: 'euro banknote' },
      { emoji: '💷', name: 'pound banknote' },
      { emoji: '💳', name: 'credit card' },
      { emoji: '🧾', name: 'receipt' },
      { emoji: '💎', name: 'gem stone' },
      { emoji: '⚖️', name: 'balance scale' },
      { emoji: '🪜', name: 'ladder' },
      { emoji: '🧰', name: 'toolbox' },
      { emoji: '🔧', name: 'wrench' },
      { emoji: '🔨', name: 'hammer' },
      { emoji: '⚒️', name: 'hammer and pick' },
      { emoji: '🛠️', name: 'hammer and wrench' },
      { emoji: '⛏️', name: 'pick' },
      { emoji: '🔩', name: 'nut and bolt' },
      { emoji: '⚙️', name: 'gear' },
      { emoji: '🪚', name: 'carpentry saw' },
      { emoji: '🔫', name: 'water pistol' },
      { emoji: '🪃', name: 'boomerang' },
      { emoji: '🏹', name: 'bow and arrow' },
      { emoji: '🛡️', name: 'shield' },
      { emoji: '🪓', name: 'axe' },
      { emoji: '🔪', name: 'kitchen knife' },
      { emoji: '🗡️', name: 'dagger' },
      { emoji: '⚔️', name: 'crossed swords' }
    ]
  },
  flags: {
    name: 'Flags',
    icon: Flag,
    emojis: [
      { emoji: '🏁', name: 'checkered flag' },
      { emoji: '🚩', name: 'triangular flag' },
      { emoji: '🎌', name: 'crossed flags' },
      { emoji: '🏴', name: 'black flag' },
      { emoji: '🏳️', name: 'white flag' },
      { emoji: '🏳️‍🌈', name: 'rainbow flag' },
      { emoji: '🏳️‍⚧️', name: 'transgender flag' },
      { emoji: '🏴‍☠️', name: 'pirate flag' },
      { emoji: '🇺🇸', name: 'flag united states' },
      { emoji: '🇬🇧', name: 'flag united kingdom' },
      { emoji: '🇨🇦', name: 'flag canada' },
      { emoji: '🇦🇺', name: 'flag australia' },
      { emoji: '🇩🇪', name: 'flag germany' },
      { emoji: '🇫🇷', name: 'flag france' },
      { emoji: '🇪🇸', name: 'flag spain' },
      { emoji: '🇮🇹', name: 'flag italy' },
      { emoji: '🇯🇵', name: 'flag japan' },
      { emoji: '🇰🇷', name: 'flag south korea' },
      { emoji: '🇨🇳', name: 'flag china' },
      { emoji: '🇮🇳', name: 'flag india' },
      { emoji: '🇧🇷', name: 'flag brazil' },
      { emoji: '🇷🇺', name: 'flag russia' },
      { emoji: '🇲🇽', name: 'flag mexico' },
      { emoji: '🇿🇦', name: 'flag south africa' }
    ]
  }
} as const

export function EmojiPicker({ onEmojiSelect, onClose, isOpen, position = 'bottom' }: EmojiPickerProps) {
  const [activeCategory, setActiveCategory] = useState<keyof typeof EMOJI_CATEGORIES>('smileys')
  const [searchTerm, setSearchTerm] = useState('')
  const pickerRef = useRef<HTMLDivElement>(null)

  // Close picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Filter emojis based on search
  const filteredEmojis = searchTerm
    ? Object.values(EMOJI_CATEGORIES).flatMap(category =>
        category.emojis.filter(emoji =>
          emoji.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : EMOJI_CATEGORIES[activeCategory].emojis

  const handleEmojiClick = (emoji: string) => {
    onEmojiSelect(emoji)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      ref={pickerRef}
      className={`absolute z-50 w-80 max-w-[calc(100vw-2rem)] bg-white border border-secondary-200 rounded-lg shadow-lg ${
        position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
      } right-0 max-h-96 overflow-hidden`}
    >
      {/* Search bar */}
      <div className="p-3 border-b border-secondary-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            type="text"
            placeholder="Search emojis..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 text-sm border border-secondary-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Categories (hide when searching) */}
      {!searchTerm && (
        <div className="flex border-b border-secondary-100">
          {Object.entries(EMOJI_CATEGORIES).map(([key, category]) => {
            const IconComponent = category.icon
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key as keyof typeof EMOJI_CATEGORIES)}
                className={`flex-1 p-2 hover:bg-secondary-50 transition-colors ${
                  activeCategory === key ? 'bg-primary-50 text-primary-600' : 'text-secondary-600'
                }`}
                title={category.name}
              >
                <IconComponent className="w-4 h-4 mx-auto" />
              </button>
            )
          })}
        </div>
      )}

      {/* Emoji grid */}
      <div className="p-3 h-64 overflow-y-auto">
        {filteredEmojis.length === 0 ? (
          <div className="text-center text-secondary-500 py-8">
            <p className="text-sm">No emojis found</p>
            <p className="text-xs">Try a different search term</p>
          </div>
        ) : (
          <div className="grid grid-cols-8 gap-1">
            {filteredEmojis.map((emojiData, index) => (
              <button
                key={`${emojiData.emoji}-${index}`}
                onClick={() => handleEmojiClick(emojiData.emoji)}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-secondary-100 transition-colors text-xl"
                title={emojiData.name}
              >
                {emojiData.emoji}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quick reactions footer */}
      <div className="p-2 border-t border-secondary-100 bg-secondary-50">
        <div className="flex space-x-1">
          {['👍', '👎', '❤️', '😂', '😮', '😢', '😡', '🎉'].map((emoji) => (
            <button
              key={emoji}
              onClick={() => handleEmojiClick(emoji)}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-white transition-colors text-lg"
              title="Quick reaction"
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}